import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Fade,
  Grow,
  alpha,
  styled,
  useTheme,
} from "@mui/material";
import { useSnackbar } from "./SnackbarProvider";
import { useLoading } from "./LoadingProvider";
import {
  Visibility,
  VisibilityOff,
  Close,
  Login as LoginIcon,
} from "@mui/icons-material";
import { useLoginMutation } from "services/authApi";

// Styled components for modern look
const LoginContainer = styled(Container)(({ theme }) => ({
  width: "100%",
  maxWidth: "450px",
  animation: "slideUp 0.5s ease-out",
  "@keyframes slideUp": {
    "0%": { opacity: 0, transform: "translateY(30px)" },
    "100%": { opacity: 1, transform: "translateY(0)" },
  },
}));

const LoginPaper = styled(Paper)(({ theme }) => ({
  backgroundColor: "white",
  borderRadius: "24px",
  padding: theme.spacing(5),
  boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "6px",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  },
}));

const LoginHeader = styled(Box)(({ theme }) => ({
  textAlign: "center",
  marginBottom: theme.spacing(4),
  position: "relative",
}));

const LoginTitle = styled(Typography)(({ theme }) => ({
  fontSize: "2rem",
  fontWeight: 700,
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  fontFamily: "'Poppins', sans-serif",
  marginBottom: theme.spacing(1),
}));

const LoginSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: "0.9rem",
  color: "#7f8c8d",
  fontWeight: 400,
  fontFamily: "'Inter', sans-serif",
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    transition: "all 0.3s ease",
    "&:hover": {
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "#667eea",
        borderWidth: "2px",
      },
    },
    "&.Mui-focused": {
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "#667eea",
        borderWidth: "2px",
      },
    },
  },
  "& .MuiInputLabel-root": {
    fontFamily: "'Inter', sans-serif",
    "&.Mui-focused": {
      color: "#667eea",
    },
  },
}));

const GradientButton = styled(Button)(({ theme }) => ({
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  color: "white",
  padding: theme.spacing(1.5),
  borderRadius: "12px",
  fontWeight: 600,
  fontSize: "1rem",
  textTransform: "none",
  transition: "all 0.3s ease",
  boxShadow: "0 8px 25px rgba(102, 126, 234, 0.3)",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: "-100%",
    width: "100%",
    height: "100%",
    background:
      "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
    transition: "left 0.7s ease",
  },
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 12px 35px rgba(102, 126, 234, 0.4)",
    "&::before": {
      left: "100%",
    },
  },
  "& .MuiButton-startIcon": {
    transition: "transform 0.3s ease",
  },
  "&:hover .MuiButton-startIcon": {
    transform: "scale(1.1)",
  },
}));

const OutlineButton = styled(Button)(({ theme }) => ({
  border: "2px solid",
  borderColor: alpha("#667eea", 0.3),
  color: "#667eea",
  padding: theme.spacing(1.5),
  borderRadius: "12px",
  fontWeight: 600,
  fontSize: "1rem",
  textTransform: "none",
  transition: "all 0.3s ease",
  "&:hover": {
    borderColor: "#667eea",
    backgroundColor: alpha("#667eea", 0.04),
    transform: "translateY(-2px)",
  },
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(2),
  right: theme.spacing(2),
  color: "#7f8c8d",
  transition: "all 0.3s ease",
  "&:hover": {
    color: "#667eea",
    backgroundColor: alpha("#667eea", 0.1),
    transform: "rotate(90deg)",
  },
}));

function LoginForm() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [loginMutation, { isLoading }] = useLoginMutation();

  const showSnackbar = useSnackbar();
  const { startLoading, stopLoading } = useLoading();
  const navigate = useNavigate();
  const theme = useTheme();

  const handlePasswordChange = (event) => {
    setPassword(event.target.value);
    if (formErrors.password) {
      setFormErrors({ ...formErrors, password: "" });
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleClose = () => {
    navigate("/");
  };

  const validateForm = () => {
    const errors = {};
    if (!username.trim()) {
      errors.username = "Username is required";
    }
    if (!password) {
      errors.password = "Password is required";
    } else if (password.length < 6) {
      errors.password = "Password must be at least 6 characters";
    }
    return errors;
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleSubmit(event);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      showSnackbar("Please fix the form errors", "error");
      return;
    }

    startLoading();

    try {
      const user = { username, password };
      const result = await loginMutation(user).unwrap();
      const { access, refresh } = result;
      localStorage.setItem("access_token", access);
      localStorage.setItem("refresh_token", refresh);
      showSnackbar("Successfully logged in!", "success");
      navigate("/dashboard");
    } catch (error) {
      console.error("Login failed:", error);
      showSnackbar(
        error?.data?.detail || "Invalid credentials. Please try again.",
        "error"
      );
      setFormErrors({
        username: "Invalid credentials",
        password: "Invalid credentials",
      });
    } finally {
      stopLoading();
    }
  };

  const handleInputChange = (setter, field) => (event) => {
    setter(event.target.value);
    if (formErrors[field]) {
      setFormErrors({ ...formErrors, [field]: "" });
    }
  };

  return (
    <Fade in={true} timeout={500}>
      <LoginContainer>
        <LoginPaper elevation={0}>
          <CloseButton onClick={handleClose}>
            <Close />
          </CloseButton>

          <LoginHeader>
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{
                  width: "60px",
                  height: "60px",
                  borderRadius: "50%",
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 16px",
                  boxShadow: "0 8px 20px rgba(102, 126, 234, 0.3)",
                }}
              >
                <LoginIcon sx={{ color: "white", fontSize: "2rem" }} />
              </Box>
            </Box>
            <LoginTitle variant="h4">Welcome Back</LoginTitle>
            <LoginSubtitle variant="body2">
              Sign in to access your caregiver portal
            </LoginSubtitle>
          </LoginHeader>

          <Box component="form" onSubmit={handleSubmit}>
            <Box sx={{ mb: 3 }}>
              <StyledTextField
                label="Username"
                value={username}
                onChange={handleInputChange(setUsername, "username")}
                onKeyPress={handleKeyPress}
                variant="outlined"
                fullWidth
                error={!!formErrors.username}
                helperText={formErrors.username}
                InputProps={{
                  style: { fontFamily: "'Inter', sans-serif" },
                }}
              />
            </Box>

            <Box sx={{ mb: 4 }}>
              <StyledTextField
                label="Password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={handlePasswordChange}
                onKeyPress={handleKeyPress}
                variant="outlined"
                fullWidth
                error={!!formErrors.password}
                helperText={formErrors.password}
                InputProps={{
                  style: { fontFamily: "'Inter', sans-serif" },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={togglePasswordVisibility}
                        edge="end"
                        sx={{
                          color: "#667eea",
                          "&:hover": {
                            backgroundColor: alpha("#667eea", 0.1),
                          },
                        }}
                      >
                        {showPassword ? (
                          <VisibilityOff fontSize="small" />
                        ) : (
                          <Visibility fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <GradientButton
                type="submit"
                fullWidth
                startIcon={<LoginIcon />}
                disabled={isLoading}
              >
                {isLoading ? "Signing In..." : "Sign In"}
              </GradientButton>
            </Box>

            <Box sx={{ mb: 2 }}>
              <OutlineButton fullWidth onClick={handleClose}>
                Cancel
              </OutlineButton>
            </Box>

            <Box sx={{ textAlign: "center", mt: 3 }}>
              <Typography
                variant="body2"
                sx={{
                  color: "#7f8c8d",
                  fontFamily: "'Inter', sans-serif",
                  "& a": {
                    color: "#667eea",
                    textDecoration: "none",
                    fontWeight: 500,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      color: "#764ba2",
                      textDecoration: "underline",
                    },
                  },
                }}
              >
                <Link to="/forgot-password">Forgot Password?</Link>
              </Typography>
            </Box>

            <Box
              sx={{
                mt: 4,
                pt: 3,
                borderTop: `1px solid ${alpha("#000", 0.1)}`,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "#7f8c8d",
                  textAlign: "center",
                  fontFamily: "'Inter', sans-serif",
                  "& a": {
                    color: "#667eea",
                    fontWeight: 600,
                    textDecoration: "none",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  },
                }}
              >
                Don't have an account? <Link to="/register">Sign up now</Link>
              </Typography>
            </Box>
          </Box>
        </LoginPaper>
      </LoginContainer>
    </Fade>
  );
}

export default LoginForm;
